import fs from 'node:fs'
import path from 'node:path'
import fse from 'fs-extra'

/**
 * Ensure a directory exists, creating it recursively if needed.
 */
export function ensureDir(dirPath: string): void {
  fse.ensureDirSync(dirPath)
}

/**
 * Write a file, creating parent directories as needed.
 * Will NOT overwrite an existing file unless `force` is true.
 * Returns true if the file was written, false if skipped.
 */
export function safeWriteFile(filePath: string, content: string, force = false): boolean {
  if (!force && fs.existsSync(filePath)) {
    return false
  }
  ensureDir(path.dirname(filePath))
  fs.writeFileSync(filePath, content, 'utf-8')
  return true
}

/**
 * Copy a single file, creating parent directories as needed.
 * Will NOT overwrite an existing file unless `force` is true.
 */
export function safeCopyFile(src: string, dest: string, force = false): boolean {
  if (!force && fs.existsSync(dest)) {
    return false
  }
  ensureDir(path.dirname(dest))
  fs.copyFileSync(src, dest)
  return true
}

/**
 * Recursively copy a directory of templates.
 * Skips existing files unless `force` is true.
 * Returns lists of written and skipped file paths.
 */
export function copyTemplateDir(
  srcDir: string,
  destDir: string,
  force = false
): { written: string[]; skipped: string[] } {
  const written: string[] = []
  const skipped: string[] = []

  function walk(currentSrc: string, currentDest: string): void {
    const entries = fs.readdirSync(currentSrc, { withFileTypes: true })
    for (const entry of entries) {
      const srcPath = path.join(currentSrc, entry.name)
      const destPath = path.join(currentDest, entry.name)

      if (entry.isDirectory()) {
        walk(srcPath, destPath)
      } else {
        if (safeCopyFile(srcPath, destPath, force)) {
          written.push(destPath)
        } else {
          skipped.push(destPath)
        }
      }
    }
  }

  walk(srcDir, destDir)
  return { written, skipped }
}

/**
 * Append content to a file if the marker text is not already present.
 * Creates the file if it doesn't exist.
 * Returns true if content was appended.
 */
export function appendToFile(filePath: string, content: string, marker: string): boolean {
  if (fs.existsSync(filePath)) {
    const existing = fs.readFileSync(filePath, 'utf-8')
    if (existing.includes(marker)) {
      return false
    }
    fs.writeFileSync(filePath, `${existing.trimEnd()}\n${content}\n`, 'utf-8')
  } else {
    ensureDir(path.dirname(filePath))
    fs.writeFileSync(filePath, `${content}\n`, 'utf-8')
  }
  return true
}
